import type { KnownNotificationResource, NotificationActorPayload, NotificationResource } from '../../../api/notifications'
import { getProjectStatusLabel } from '../../../consts/projectTypeOptions'
import {
  isDeletedUser,
  resolveUserAvatarUrl,
  resolveUserDisplayName,
} from '../../../utils/deletedUserDisplay'
import { getMediaSourceUrl } from '../../../utils/media'
import { getActorPathKey } from './notificationNav'

export function mediaSourceUrl(url: string | null | undefined): string | undefined {
  return getMediaSourceUrl(url) ?? undefined
}

export function resolveNotificationActor(
  payload: { actor?: NotificationActorPayload | null } | null | undefined,
  actorUserId?: number | null,
): NotificationActorPayload {
  const raw = payload?.actor
  if (raw && typeof raw === 'object') {
    return raw
  }

  return {
    id: typeof actorUserId === 'number' && actorUserId > 0 ? actorUserId : null,
    fullname: 'Пользователь',
    avatar_url: null,
  }
}

export function notificationActorDisplayName(
  actor: NotificationActorPayload | null | undefined,
): string {
  return resolveUserDisplayName(
    actor
      ? {
          deleted: actor.deleted,
          fullname: actor.fullname,
          avatar: actor.avatar_url,
          slug: actor.slug ?? undefined,
          id: actor.id,
        }
      : null,
  )
}

export function notificationActorAvatarSrc(
  actor: NotificationActorPayload | null | undefined,
): string | undefined {
  const fromDeletedGuard = resolveUserAvatarUrl(
    actor ? { deleted: actor.deleted, avatar: actor.avatar_url } : null,
  )
  if (fromDeletedGuard) {
    return fromDeletedGuard
  }

  return mediaSourceUrl(actor?.avatar_url)
}

export function notificationActorPathKey(
  actor: NotificationActorPayload | null | undefined,
  actorUserId?: number | null,
): string {
  if (isDeletedUser(actor ? { deleted: actor.deleted } : null)) {
    return ''
  }

  return getActorPathKey(actor, actorUserId)
}

function trimString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function payloadRecord(n: NotificationResource): Record<string, unknown> | null {
  if (!('payload' in n) || typeof n.payload !== 'object' || n.payload === null) return null
  return n.payload as Record<string, unknown>
}

function actorNameFromPayload(payload: Record<string, unknown> | null): string {
  const actor = payload?.actor
  if (typeof actor === 'object' && actor !== null) {
    const record = actor as Record<string, unknown>
    if (record.deleted === true) {
      return resolveUserDisplayName({ deleted: true })
    }
    const name = trimString(record.fullname)
    if (name.length > 0) return name
  }
  return 'Пользователь'
}

function memberNameFromPayload(payload: Record<string, unknown> | null): string {
  const member = payload?.member
  if (typeof member === 'object' && member !== null) {
    const record = member as Record<string, unknown>
    if (record.deleted === true) {
      return resolveUserDisplayName({ deleted: true })
    }
    const name = trimString(record.fullname)
    if (name.length > 0) return name
  }
  return 'Участник'
}

function authorNameFromPayload(payload: Record<string, unknown> | null): string {
  const author = payload?.author
  if (typeof author === 'object' && author !== null) {
    const name = trimString((author as Record<string, unknown>).fullname)
    if (name.length > 0) return name
  }
  return 'Автор'
}

function teamNameFromPayload(payload: Record<string, unknown> | null): string {
  const team = payload?.team
  if (typeof team === 'object' && team !== null) {
    const record = team as Record<string, unknown>
    const title = trimString(record.title)
    if (title.length > 0) return title
    const name = trimString(record.name)
    if (name.length > 0) return name
  }
  return 'команда'
}

function projectTitleFromPayload(payload: Record<string, unknown> | null, field: 'title' | 'name' = 'title'): string {
  const project = payload?.project
  if (typeof project !== 'object' || project === null) return field === 'name' ? 'проект' : 'проект'
  const value = trimString((project as Record<string, unknown>)[field])
  if (value.length > 0) return value
  const fallback = trimString((project as Record<string, unknown>).title)
  if (fallback.length > 0) return fallback
  const name = trimString((project as Record<string, unknown>).name)
  if (name.length > 0) return name
  return 'проект'
}

function socialOrderTitleFromPayload(payload: Record<string, unknown> | null): string {
  const socialOrder = payload?.social_order
  if (typeof socialOrder !== 'object' || socialOrder === null) return 'соцзаказ'
  const title = trimString((socialOrder as Record<string, unknown>).title)
  if (title.length > 0) return title
  return 'соцзаказ'
}

function growthToolTitleFromPayload(payload: Record<string, unknown> | null): string {
  const growthTool = payload?.growth_tool
  if (typeof growthTool !== 'object' || growthTool === null) return 'инструмент роста'
  const title = trimString((growthTool as Record<string, unknown>).title)
  if (title.length > 0) return title
  return 'инструмент роста'
}

export function getNotificationFallbackTitle(n: NotificationResource): string {
  if (typeof n.title === 'string' && n.title.trim().length > 0) return n.title
  switch (n.type) {
    case 'project.join_request.created':
      return 'Новая заявка в проект'
    case 'project.join_request.decided':
      return 'Решение по заявке'
    case 'project.join_request.canceled':
      return 'Заявка отменена'
    case 'project.invitation.created':
      return 'Приглашение в проект'
    case 'project.invitation.decided':
      return 'Решение по приглашению'
    case 'project.invitation.cancelled':
      return 'Приглашение отменено'
    case 'project.team_invitation.created':
      return 'Приглашение команды в проект'
    case 'project.team_invitation.decided':
      return 'Решение по приглашению команды'
    case 'project.team_invitation.cancelled':
      return 'Приглашение команды отменено'
    case 'project.team.removed':
      return 'Команду исключили из проекта'
    case 'project.subscription.created':
      return 'Новая подписка на проект'
    case 'project.subscription.deleted':
      return 'Отписка от проекта'
    case 'team.subscription.created':
      return 'Новая подписка на команду'
    case 'team.subscription.deleted':
      return 'Отписка от команды'
    case 'team.member.left':
      return 'Участник вышел из команды'
    case 'team.member.removed':
      return 'Исключение из команды'
    case 'team.member.removed.broadcast':
      return 'Участник исключён'
    case 'team.join_request.created':
      return 'Новая заявка в команду'
    case 'team.join_request.decided':
      return 'Решение по заявке в команду'
    case 'team.join_request.canceled':
      return 'Заявка в команду отменена'
    case 'team.invitation.created':
      return 'Приглашение в команду'
    case 'team.invitation.decided':
      return 'Решение по приглашению в команду'
    case 'team.invitation.cancelled':
      return 'Приглашение в команду отменено'
    case 'team.admin.granted':
      return 'Права администратора'
    case 'team.admin.revoked':
      return 'Снятие прав администратора'
    case 'project.admin.granted':
      return 'Права администратора'
    case 'project.admin.revoked':
      return 'Снятие прав администратора'
    case 'project.team_space.changed':
      return 'Командное пространство'
    case 'project.restriction.applied':
      return 'Ограничение доступа'
    case 'project.restriction.removed':
      return 'Ограничение снято'
    case 'project.member.removed':
      return 'Исключение из проекта'
    case 'project.like.summary':
      return 'Лайки проекта'
    case 'project.comment.created':
      return 'Новый комментарий'
    case 'project.comment.replied':
      return 'Ответ в обсуждении'
    case 'project.comment.liked':
      return 'Лайк комментария'
    case 'social_order.comment.created':
      return 'Новый комментарий'
    case 'social_order.comment.replied':
      return 'Ответ в обсуждении'
    case 'social_order.comment.liked':
      return 'Лайк комментария'
    case 'social_order.like.summary':
      return 'Лайки соцзаказа'
    case 'social_order.subscription.created':
      return 'Новая подписка на соцзаказ'
    case 'social_order.subscription.deleted':
      return 'Отписка от соцзаказа'
    case 'social_order.restriction.applied':
      return 'Ограничение доступа'
    case 'social_order.restriction.removed':
      return 'Ограничение снято'
    case 'growth_tool.restriction.applied':
      return 'Ограничение доступа'
    case 'growth_tool.restriction.removed':
      return 'Ограничение снято'
    case 'user_followed_author':
      return 'Новые подписчики'
    case 'user_followed_author_project_published':
      return 'Новый проект автора'
    case 'user_followed_author_project_status_changed':
      return 'Изменение статуса проекта'
    case 'user_followed_author_project_recruitment_opened':
      return 'Открыт набор в проект'
    case 'user_followed_author_project_recruitment_updated':
      return 'Обновлен набор в проект'
    case 'social_order.completion.changed':
      return 'Статус соцзаказа'
    case 'social_order.linked_project.created':
      return 'Проект по соцзаказу'
    case 'social_order.linked_project.created.author':
      return 'Проект по вашему соцзаказу'
    default:
      return 'Уведомление'
  }
}

export function getNotificationPreviewMessage(n: NotificationResource): string {
  const payload = payloadRecord(n)

  switch (n.type) {
    case 'project.join_request.created': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      return `${actor} подал(а) заявку в «${projectTitle}»`
    }
    case 'project.join_request.decided': {
      const nn = n as Extract<KnownNotificationResource, { type: 'project.join_request.decided' }>
      const status = nn.payload?.join_request?.status
      const projectTitle = projectTitleFromPayload(payload)
      if (status === 'approved') return `Заявка в «${projectTitle}» одобрена`
      if (status === 'rejected') return `Заявка в «${projectTitle}» отклонена`
      return `Решение по заявке в «${projectTitle}»`
    }
    case 'project.join_request.canceled': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      const hasActor = typeof payload?.actor === 'object' && payload.actor !== null
      return hasActor
        ? `${actor} отменил(а) заявку в «${projectTitle}»`
        : `Заявка в «${projectTitle}» отменена`
    }
    case 'project.invitation.created': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      return `${actor} пригласил(а) в «${projectTitle}»`
    }
    case 'project.invitation.decided': {
      const nn = n as Extract<KnownNotificationResource, { type: 'project.invitation.decided' }>
      const status = nn.payload?.invitation?.status
      const projectTitle = projectTitleFromPayload(payload)
      if (status === 'accepted') return `Приглашение в «${projectTitle}» принято`
      if (status === 'declined') return `Приглашение в «${projectTitle}» отклонено`
      return `Решение по приглашению в «${projectTitle}»`
    }
    case 'project.invitation.cancelled': {
      const actor = actorNameFromPayload(payload)
      return `${actor} отменил(а) приглашение`
    }
    case 'project.team_invitation.created': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      return `${actor} пригласил(а) команду «${teamName}» в «${projectTitle}»`
    }
    case 'project.team_invitation.decided': {
      const nn = n as Extract<KnownNotificationResource, { type: 'project.team_invitation.decided' }>
      const status = nn.payload?.invitation?.status
      const projectTitle = projectTitleFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      if (status === 'accepted') return `Команда «${teamName}» приняла приглашение в «${projectTitle}»`
      if (status === 'declined') return `Команда «${teamName}» отклонила приглашение в «${projectTitle}»`
      return `Решение по приглашению «${teamName}» в «${projectTitle}»`
    }
    case 'project.team_invitation.cancelled': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      return `${actor} отменил(а) приглашение команды «${teamName}»`
    }
    case 'project.team.removed': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      return `${actor} исключил(а) команду «${teamName}» из проекта «${projectTitle}»`
    }
    case 'project.subscription.created': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      return `${actor} подписался на «${projectTitle}»`
    }
    case 'project.subscription.deleted': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      return `${actor} отписался от «${projectTitle}»`
    }
    case 'team.subscription.created': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      return `${actor} подписался на «${teamName}»`
    }
    case 'team.subscription.deleted': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      return `${actor} отписался от «${teamName}»`
    }
    case 'team.member.left': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      const message = trimString(payload?.message)
      return message.length > 0
        ? `${actor} вышел из «${teamName}» — ${message}`
        : `${actor} вышел из «${teamName}»`
    }
    case 'team.member.removed': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      const message = trimString(payload?.message)
      return message.length > 0
        ? `${actor} исключил(а) вас из «${teamName}» — ${message}`
        : `${actor} исключил(а) вас из «${teamName}»`
    }
    case 'team.member.removed.broadcast': {
      const actor = actorNameFromPayload(payload)
      const member = memberNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      return `${actor} исключил(а) ${member} из «${teamName}»`
    }
    case 'team.join_request.created': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      return `Заявка: ${actor} → ${teamName}`
    }
    case 'team.join_request.decided': {
      const nn = n as Extract<KnownNotificationResource, { type: 'team.join_request.decided' }>
      const status = nn.payload?.join_request?.status
      const teamName = teamNameFromPayload(payload)
      if (status === 'approved') return `Заявка в «${teamName}» одобрена`
      if (status === 'rejected') return `Заявка в «${teamName}» отклонена`
      return `Решение по заявке в «${teamName}»`
    }
    case 'team.join_request.canceled': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      const hasActor = typeof payload?.actor === 'object' && payload.actor !== null
      return hasActor
        ? `${actor} отменил(а) заявку в «${teamName}»`
        : `Заявка в «${teamName}» отменена`
    }
    case 'team.invitation.created': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      return `Приглашение: ${teamName} ← ${actor}`
    }
    case 'team.invitation.decided': {
      const nn = n as Extract<KnownNotificationResource, { type: 'team.invitation.decided' }>
      const status = nn.payload?.invitation?.status
      const teamName = teamNameFromPayload(payload)
      if (status === 'accepted') return `Приглашение в «${teamName}» принято`
      if (status === 'declined') return `Приглашение в «${teamName}» отклонено`
      return `Решение по приглашению в «${teamName}»`
    }
    case 'team.invitation.cancelled': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      return `${actor} отменил(а) приглашение в «${teamName}»`
    }
    case 'team.admin.granted': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      return `${actor} назначил(а) вас администратором «${teamName}»`
    }
    case 'team.admin.revoked': {
      const actor = actorNameFromPayload(payload)
      const teamName = teamNameFromPayload(payload)
      return `${actor} снял(а) с вас права администратора «${teamName}»`
    }
    case 'project.admin.granted': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      return `${actor} назначил(а) вас администратором «${projectTitle}»`
    }
    case 'project.admin.revoked': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      return `${actor} снял(а) с вас права администратора «${projectTitle}»`
    }
    case 'project.team_space.changed': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      const change = typeof payload?.change === 'string' ? payload.change : ''
      if (change === 'links_updated') return `${actor} обновил(а) ссылки команды в «${projectTitle}»`
      if (change === 'card_created') return `${actor} добавил(а) карточку в командном пространстве «${projectTitle}»`
      if (change === 'card_deleted') return `${actor} удалил(а) карточку в командном пространстве «${projectTitle}»`
      return `${actor} обновил(а) командное пространство «${projectTitle}»`
    }
    case 'project.restriction.applied': {
      const nn = n as Extract<KnownNotificationResource, { type: 'project.restriction.applied' }>
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      const level = nn.payload?.restriction?.level
      if (level === 'full') return `${actor} ограничил(а) ваш доступ к «${projectTitle}»`
      if (level === 'comments') return `${actor} запретил(а) вам комментировать «${projectTitle}»`
      return `${actor} ограничил(а) ваш доступ к «${projectTitle}»`
    }
    case 'project.restriction.removed': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      return `${actor} снял(а) ограничения доступа к «${projectTitle}»`
    }
    case 'project.member.removed': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      return `${actor} исключил(а) вас из проекта «${projectTitle}»`
    }
    case 'project.like.summary': {
      const nn = n as Extract<KnownNotificationResource, { type: 'project.like.summary' }>
      const lastActor = nn.payload?.last_actor
      const lastActorName =
        typeof lastActor?.fullname === 'string' && lastActor.fullname.trim().length > 0
          ? lastActor.fullname.trim()
          : 'Пользователь'
      const likesCountRaw = Number(nn.payload?.likes_count ?? 0)
      const likesCount = Number.isFinite(likesCountRaw) ? Math.max(0, likesCountRaw) : 0
      const more = Math.max(likesCount - 1, 0)
      const projectTitle = projectTitleFromPayload(payload)
      if (more > 0) return `${lastActorName} и ещё ${more} поставили лайк «${projectTitle}»`
      return `${lastActorName} поставил(а) лайк «${projectTitle}»`
    }
    case 'project.comment.created':
    case 'project.comment.replied':
    case 'project.comment.liked': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      const action =
        n.type === 'project.comment.created'
          ? 'оставил комментарий'
          : n.type === 'project.comment.replied'
            ? 'ответил в обсуждении'
            : 'поставил лайк комментарию'
      return `${actor} ${action} в «${projectTitle}»`
    }
    case 'social_order.comment.created':
    case 'social_order.comment.replied':
    case 'social_order.comment.liked': {
      const actor = actorNameFromPayload(payload)
      const socialOrderTitle = socialOrderTitleFromPayload(payload)
      const action =
        n.type === 'social_order.comment.created'
          ? 'оставил комментарий'
          : n.type === 'social_order.comment.replied'
            ? 'ответил в обсуждении'
            : 'поставил лайк комментарию'
      return `${actor} ${action} в «${socialOrderTitle}»`
    }
    case 'social_order.like.summary': {
      const nn = n as Extract<KnownNotificationResource, { type: 'social_order.like.summary' }>
      const lastActor = nn.payload?.last_actor
      const lastActorName =
        typeof lastActor?.fullname === 'string' && lastActor.fullname.trim().length > 0
          ? lastActor.fullname.trim()
          : 'Пользователь'
      const likesCountRaw = Number(nn.payload?.likes_count ?? 0)
      const likesCount = Number.isFinite(likesCountRaw) ? Math.max(0, likesCountRaw) : 0
      const more = Math.max(likesCount - 1, 0)
      const socialOrderTitle = socialOrderTitleFromPayload(payload)
      if (more > 0) return `${lastActorName} и ещё ${more} поставили лайк «${socialOrderTitle}»`
      return `${lastActorName} поставил(а) лайк «${socialOrderTitle}»`
    }
    case 'social_order.subscription.created': {
      const actor = actorNameFromPayload(payload)
      const socialOrderTitle = socialOrderTitleFromPayload(payload)
      return `${actor} подписался на «${socialOrderTitle}»`
    }
    case 'social_order.subscription.deleted': {
      const actor = actorNameFromPayload(payload)
      const socialOrderTitle = socialOrderTitleFromPayload(payload)
      return `${actor} отписался от «${socialOrderTitle}»`
    }
    case 'social_order.restriction.applied': {
      const nn = n as Extract<KnownNotificationResource, { type: 'social_order.restriction.applied' }>
      const actor = actorNameFromPayload(payload)
      const socialOrderTitle = socialOrderTitleFromPayload(payload)
      const level = nn.payload?.restriction?.level
      if (level === 'full') return `${actor} ограничил(а) ваш доступ к «${socialOrderTitle}»`
      if (level === 'comments') return `${actor} запретил(а) вам комментировать «${socialOrderTitle}»`
      return `${actor} ограничил(а) ваш доступ к «${socialOrderTitle}»`
    }
    case 'social_order.restriction.removed': {
      const actor = actorNameFromPayload(payload)
      const socialOrderTitle = socialOrderTitleFromPayload(payload)
      return `${actor} снял(а) ограничения доступа к «${socialOrderTitle}»`
    }
    case 'growth_tool.restriction.applied': {
      const nn = n as Extract<KnownNotificationResource, { type: 'growth_tool.restriction.applied' }>
      const actor = actorNameFromPayload(payload)
      const growthToolTitle = growthToolTitleFromPayload(payload)
      const level = nn.payload?.restriction?.level
      if (level === 'full') return `${actor} ограничил(а) ваш доступ к «${growthToolTitle}»`
      if (level === 'comments') return `${actor} запретил(а) вам комментировать «${growthToolTitle}»`
      return `${actor} ограничил(а) ваш доступ к «${growthToolTitle}»`
    }
    case 'growth_tool.restriction.removed': {
      const actor = actorNameFromPayload(payload)
      const growthToolTitle = growthToolTitleFromPayload(payload)
      return `${actor} снял(а) ограничения доступа к «${growthToolTitle}»`
    }
    case 'user_followed_author': {
      const nn = n as Extract<KnownNotificationResource, { type: 'user_followed_author' }>
      const lastFollower = nn.payload?.last_follower
      const lastFollowerName =
        typeof lastFollower?.fullname === 'string' && lastFollower.fullname.trim().length > 0
          ? lastFollower.fullname.trim()
          : 'Пользователь'
      const followersCountRaw = Number(nn.payload?.followers_count ?? 0)
      const followersCount = Number.isFinite(followersCountRaw) ? Math.max(0, followersCountRaw) : 0
      const effectiveFollowersCount = followersCount > 0 ? followersCount : lastFollower ? 1 : 0
      if (effectiveFollowersCount === 1) return `На вас подписался(ась) ${lastFollowerName}`
      if (effectiveFollowersCount > 1) return `На вас подписались ${effectiveFollowersCount} человек`
      return 'Новые подписчики'
    }
    case 'user_followed_author_project_published': {
      const author = authorNameFromPayload(payload)
      const projectName = projectTitleFromPayload(payload, 'name')
      return `${author} опубликовал(а) «${projectName}»`
    }
    case 'user_followed_author_project_status_changed': {
      const nn = n as Extract<KnownNotificationResource, { type: 'user_followed_author_project_status_changed' }>
      const author = authorNameFromPayload(payload)
      const projectName = projectTitleFromPayload(payload, 'name')
      const statusBefore = getProjectStatusLabel(nn.payload?.event_meta?.status_before)
      const statusAfter = getProjectStatusLabel(nn.payload?.event_meta?.status_after)
      if (statusBefore.length > 0 && statusAfter.length > 0) {
        return `${author} изменил(а) статус «${projectName}»: ${statusBefore} → ${statusAfter}`
      }
      return `${author} изменил(а) статус «${projectName}»`
    }
    case 'user_followed_author_project_recruitment_opened': {
      const author = authorNameFromPayload(payload)
      const projectName = projectTitleFromPayload(payload, 'name')
      return `${author} открыл(а) набор в «${projectName}»`
    }
    case 'user_followed_author_project_recruitment_updated': {
      const author = authorNameFromPayload(payload)
      const projectName = projectTitleFromPayload(payload, 'name')
      return `${author} обновил(а) набор в «${projectName}»`
    }
    case 'social_order.completion.changed': {
      const actor = actorNameFromPayload(payload)
      const socialOrderTitle = socialOrderTitleFromPayload(payload)
      const nn = n as Extract<KnownNotificationResource, { type: 'social_order.completion.changed' }>
      const isCompleted = Boolean(
        nn.payload?.event_meta?.is_completed ?? nn.payload?.social_order?.is_completed,
      )
      return isCompleted
        ? `${actor} отметил(а) «${socialOrderTitle}» выполненным`
        : `${actor} отметил(а), что «${socialOrderTitle}» снова ожидает реализации`
    }
    case 'social_order.linked_project.created': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      const socialOrderTitle = socialOrderTitleFromPayload(payload)
      return `${actor} опубликовал(а) проект «${projectTitle}» по соцзаказу «${socialOrderTitle}»`
    }
    case 'social_order.linked_project.created.author': {
      const actor = actorNameFromPayload(payload)
      const projectTitle = projectTitleFromPayload(payload)
      const socialOrderTitle = socialOrderTitleFromPayload(payload)
      return `${actor} опубликовал(а) проект «${projectTitle}» по вашему соцзаказу «${socialOrderTitle}»`
    }
    default:
      return 'Откройте уведомление для подробностей'
  }
}

export function getNotificationFallbackMessage(n: NotificationResource): string {
  if (typeof n.message === 'string' && n.message.trim().length > 0) return n.message
  return getNotificationPreviewMessage(n)
}

